import { useEffect } from 'react'
import {
  rucksack,
  useColorScheme,
  Alert,
  Button,
  IconButton,
  Box,
  Link,
} from '@black-bear-energy/rucksack'
import { MenuRounded } from '@mui/icons-material'
import { NavLink, useHistory } from 'react-router-dom'
import logo192 from '../../assets/logo192.png'
import {
  isBBEUserOrSuperAdmin,
  useAuth,
  useImpersonatingUserOrUser,
} from '../../state/modules/auth'
import {
  useDeleteImpersonation,
  useImpersonatingUser,
} from '../../state/modules/impersonations'
import { ForagerWordmark } from './ForagerWordmark'
import { HEADER_HEIGHT } from './constants'
import { useHeaderBreadcrumbs } from './HeaderBreadcrumbsContext'
import { useNavigation } from './NavigationContext'
import { UserAvatarMenu } from './UserAvatarMenu'

/** Top header bar with navigation toggle, logo, and user avatar menu. */
export function AppHeader() {
  const history = useHistory()
  const { toggleExpanded } = useNavigation()
  const headerBreadcrumbs = useHeaderBreadcrumbs()
  const { logout, user } = useAuth()
  const impersonatingUser = useImpersonatingUser()
  const impersonatingUserOrUser = useImpersonatingUserOrUser()
  const deleteImpersonation = useDeleteImpersonation()
  const { mode, setMode } = useColorScheme()

  useEffect(() => {
    setMode('light')
  }, [setMode])

  if (!mode) return null

  const canImpersonate =
    !!impersonatingUserOrUser && isBBEUserOrSuperAdmin(impersonatingUserOrUser)

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: HEADER_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingX: 2,
        background: rucksack.palette.background.navigation,
        zIndex: rucksack.zIndex.appBar,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          color="primary"
          onClick={toggleExpanded}
          sx={{
            borderRadius: rucksack.shape.borderRadius,
          }}
        >
          <MenuRounded />
        </IconButton>
        <Link
          component={NavLink}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            textDecoration: 'none',
            borderRadius: rucksack.shape.borderRadius,
            '&&:focus-visible': {
              outline: `2px solid ${rucksack.palette.primary.main}`,
              outlineOffset: '2px',
            },
          }}
        >
          <img src={logo192} alt="" height={40} width={40} />
          <ForagerWordmark />
        </Link>
        {headerBreadcrumbs && (
          <Box sx={{ marginLeft: 2 }}>{headerBreadcrumbs}</Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {impersonatingUser?.impersonatingDetails != null && (
          <Alert
            severity="info"
            action={
              <Button
                variant="text"
                color="inherit"
                size="small"
                onClick={deleteImpersonation}
              >
                Exit
              </Button>
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: rucksack.palette.info.main,
              '.MuiAlert-action': {
                paddingTop: 0,
              },
            }}
          >
            Currently in impersonation mode
          </Alert>
        )}
        <UserAvatarMenu
          logout={logout}
          canImpersonate={canImpersonate}
          onImpersonate={() => history.push('/manage/users')}
          user={user}
        />
      </Box>
    </Box>
  )
}
